import * as React from 'react';
import { TransitionStatus } from 'react-transition-group/Transition';
import styled, { css } from 'styled-components';
import CloseIcon from "@floji-internal/mui-icons/Close";
import { IconButton } from "--components/buttons/iconButton";
import { Body1 } from "--components/typography";
import { ToastAppearance } from './types';

//icons
import IconSuccess from '@floji-internal/mui-icons/CheckCircleOutline';
import IconError from '@floji-internal/mui-icons/ErrorOutline';
import IconWarning from '@floji-internal/mui-icons/Warning';
import IconInfo from '@floji-internal/mui-icons/Info_outline';

const transitionStyles = {
    entering: css`opacity: 0;`,
    entered: css`opacity: 1;`,
    exiting: css`opacity: 0;`,
    exited: css`opacity: 0;`,
};


const DEFAULT_WIDTH = 400;
const StyledToast = styled.div<{ transitionState: TransitionStatus, transitionDuration: number, hasOnClick: boolean }>`
    min-width:${DEFAULT_WIDTH}px;
    max-width:${DEFAULT_WIDTH}px;
    box-sizing:border-box;
    background:${({ theme }) => theme.palette.surface};
    border-radius:${({ theme }) => theme.radius.large};
    border:1px solid ${({ theme }) => theme.palette.ui.border};
    box-shadow:${({ theme }) => theme.shadows.low};
    display:flex;
    padding:${({ theme }) => theme.spacing(1)};


    margin-bottom:${({ theme }) => theme.spacing(1)};
    /* set transition on opacity using the duration passed in */
    transition: ${({ transitionDuration }) => `opacity ${transitionDuration}ms ease-in-out`};
    /* get the mapped css for the transition state */
    ${({ transitionState }) => transitionStyles[transitionState]}
    /* set pointer */
    cursor: ${({ hasOnClick }) => hasOnClick ? "pointer" : "default"};
    align-items:center;
`;

const StyledToastContent = styled.div`
    flex:1 1 auto;
    padding:${({ theme }) => theme.spacing(0, 1)};
    display:flex;
`
const StyledButtonContainer = styled.div`
    flex:0 0 auto;
    justify-self:flex-end;
`
const StyledIconContainer = styled.div<{ appearance: ToastAppearance }>`
    color:${({ appearance, theme }) =>
        appearance == "success" ? theme.palette.positive.main :
            appearance == "error" ? theme.palette.danger.main :
                appearance == "warning" ? theme.palette.warning.main : "#4B9DCB"
    };
`

const StyledToastContainer = styled.div<{ transitionDuration: number }>`
    display:flex;
    transition: ${({ transitionDuration }) => `height 100ms ${transitionDuration - 100}ms`};
    z-index:4000;
`;

type ToastNotificationProps = {
    transitionState: TransitionStatus,
    transitionDuration?: number
    autoDismissTimeout?: number
    autoDismiss?: boolean
    onDismiss?: () => void;
    onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    appearance: ToastAppearance
}



const ToastNotification: React.FC<ToastNotificationProps> = props => {
    const { children, transitionState, transitionDuration = 500, autoDismissTimeout = 8000, autoDismiss = true, onDismiss, onClick, appearance } = props

    const [height, setHeight] = React.useState('auto');
    const elementRef = React.useRef<HTMLDivElement>(null)
    const timeoutRef = React.useRef<number>();


    /** handle timeouts */
    React.useEffect(() => {
        if (autoDismiss && onDismiss) { // added check for onDismiss, pipeline was failing when onDismiss returns void|undefined
            timeoutRef.current = window.setTimeout(onDismiss, autoDismissTimeout);
        }
        return () => {
            if (timeoutRef) clearTimeout(timeoutRef.current);
        }
    }, []);

    React.useLayoutEffect(
        () => {
            if (transitionState === 'entered') {
                const el = elementRef.current;
                if (el) setHeight(`${el.offsetHeight}px`);
            }
            else if (transitionState === 'exiting') {
                setHeight("0px");
            }
        },
        [transitionState]
    );

    //dismiss handler to stop propagation of mouse event from interefering with main onClick event.
    const onDismissHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        if (onDismiss) onDismiss();
    }

    //if children is a string then wrap it in default <body1> else use the children as is.
    const content = typeof children === "string" ? <Body1>{children}</Body1> : children;

    return (
        <StyledToastContainer ref={elementRef} style={{ height }} transitionDuration={transitionDuration}>
            <StyledToast hasOnClick={onClick ? true : false} onClick={onClick} transitionState={transitionState} transitionDuration={transitionDuration}>
                <StyledIconContainer appearance={appearance}>
                    {
                        appearance == "success" ? <IconSuccess /> :
                            appearance == "error" ? <IconError /> :
                                appearance == "warning" ? <IconWarning /> :
                                    <IconInfo />
                    }
                </StyledIconContainer>
                <StyledToastContent>
                    {content}
                </StyledToastContent>
                {
                    onDismiss &&
                    <StyledButtonContainer>
                        <IconButton color="greyInverted" onClick={onDismissHandler} size="small" icon={<CloseIcon />} />
                    </StyledButtonContainer>
                }

            </StyledToast>
        </StyledToastContainer>
    )
}

export default ToastNotification;
export { ToastNotification, ToastNotificationProps };